import React from 'react';
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import "./components/ui-overrides.css";
import { ErrorBoundary } from './components/ErrorBoundary';

// Apply dark theme directly to the document
document.documentElement.classList.add("dark");
document.documentElement.setAttribute("data-theme", "dark");
document.body.setAttribute("data-theme", "dark");

// Force dark mode with additional overrides
document.documentElement.style.colorScheme = "dark";
document.body.style.backgroundColor = "#080810";
document.body.style.color = "#ffffff";

// Define a global type for window with our custom properties
declare global {
  interface Window {
    appErrorState?: {
      hasError: boolean;
      errorSeen: boolean;
      lastError: string;
    };
  }
}

// Add global defensive patching to prevent "s[n.symbol] is null" errors
// This catches all dynamic property lookups that might cause errors
const safeLookup = (obj: any, key: any, fallback: any = null) => {
  if (!obj || typeof obj !== 'object' || key == null) return fallback;
  try {
    return obj[key] !== undefined ? obj[key] : fallback;
  } catch (e) {
    console.warn('Prevented null property access error for key:', key);
    return fallback;
  }
};

// Apply global Symbol.prototype patch before rendering
if (typeof window !== 'undefined') {
  // Make Object.prototype[Symbol.toString] and similar methods safe
  const originalToString = Object.prototype.toString;
  Object.prototype.toString = function() {
    try {
      return originalToString.apply(this);
    } catch (e) {
      console.warn('Prevented toString error on potentially null object');
      return '[object Object]';
    }
  };

  // Safety wrapper for array/object access
  const originalValueOf = Object.prototype.valueOf;
  Object.prototype.valueOf = function() {
    try {
      return originalValueOf.apply(this);
    } catch (e) {
      console.warn('Prevented valueOf error on potentially null object');
      return {};
    }
  };

  // Patch window.Symbol to handle errors
  const originalSymbolFor = window.Symbol.for;
  window.Symbol.for = function(key) {
    try {
      return originalSymbolFor.call(window.Symbol, key);
    } catch (e) {
      console.warn('Prevented Symbol.for error:', key);
      return Symbol('fallback');
    }
  };

  // Patch array lookups (which can be used in charts)
  const originalArrayFind = Array.prototype.find;
  Array.prototype.find = function<T>(predicate: (value: T, index: number, obj: T[]) => unknown, thisArg?: any): T | undefined {
    try {
      return originalArrayFind.call(this, predicate, thisArg);
    } catch (e) {
      console.warn('Prevented error in Array.find');
      return undefined;
    }
  };
}

// Make the safeLookup available globally for emergency patching
(window as any).safeLookup = safeLookup;

// Language detection from URL
// Autopilot: Use all available locales automatically
const supportedLangs = [
  'cs','sv','hu','el','ms','id','nl','ro','uk','pl','th','fa','ur','ta','vi','it','tr','ko','jv','de','ja','ru','bn','pt','hi','fr','ar','zh','es','en'
];
const pathLang = window.location.pathname.split('/')[1];
const lang = supportedLangs.includes(pathLang) ? pathLang : 'en';

// Performant rendering
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <ErrorBoundary>
        <App lang={lang} />
      </ErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>
);
